// src/admin/components/Dashboard/AdminDashboard.jsx - Full admin dashboard
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  RiDashboardLine,
  RiUserLine,
  RiBuildingLine,
  RiTeamLine,
  RiMoneyDollarCircleLine,
  RiArrowUpLine,
  RiArrowDownLine,
  RiAddLine,
  RiCalendarLine,
  RiAlertLine,
  RiEyeLine,
  RiRefreshLine,
  RiFilterLine,
  RiDownloadLine,
  RiCheckboxCircleLine,
  RiTimeLine,
  RiNotificationLine
} from 'react-icons/ri';
import { useTheme } from '../../../contexts/ThemeContext';
import { useAuth } from '../../../contexts/AuthContext';
import api from '../../../services/api';

const AdminDashboard = () => {
  const { t } = useTranslation();
  const { isDarkMode } = useTheme();
  const { user } = useAuth();
  const navigate = useNavigate();

  // State management
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [dateRange, setDateRange] = useState('30'); // days

  // Dashboard data state
  const [dashboardData, setDashboardData] = useState({
    stats: [],
    recentTransactions: [],
    alerts: [],
    chartData: null
  });

  // Fetch dashboard data
  const fetchDashboardData = async (showRefreshIndicator = false) => {
    try {
      if (showRefreshIndicator) {
        setRefreshing(true);
      } else {
        setLoading(true);
      }

      // Fetch stats and upcoming leases
      const statsRes = await api.get('/reports/dashboard-stats');
      // Fetch recent payments
      const paymentsRes = await api.get('/reports/payment-logs', { params: { limit: 5 } });

      const statsObj = statsRes.data?.stats || {};
      const pendingCount = statsObj.pending_payments_count || 0;
      const pendingAmount = statsObj.pending_payments_amount || 0;

      const realData = {
        stats: [
          {
            title: t('total_properties'),
            value: statsObj.total_properties || '0',
            change: '+0%',
            trend: 'up',
            icon: RiBuildingLine,
            color: 'blue',
            description: t('active_properties')
          },
          {
            title: t('active_tenants'),
            value: statsObj.active_tenants || '0',
            change: '+0%',
            trend: 'up',
            icon: RiTeamLine,
            color: 'green',
            description: t('occupancy_rate_desc', { rate: statsObj.occupancy_rate || 0 })
          },
          {
            title: t('monthly_revenue'),
            value: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(statsObj.monthly_revenue || 0),
            change: '+0%',
            trend: 'up',
            icon: RiMoneyDollarCircleLine,
            color: 'purple',
            description: t('vs_last_month')
          },
          {
            title: t('pending_payments'),
            value: pendingCount.toString(),
            change: '',
            trend: pendingCount > 0 ? 'down' : 'up',
            icon: RiAlertLine,
            color: 'red',
            description: t('overdue_amount', { amount: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(pendingAmount) })
          }
        ],
        recentTransactions: (paymentsRes.data?.paymentLogs || []).map(p => ({
          id: p.paymentId,
          type: 'payment',
          description: p.apartmentNumber
            ? t('payment_desc', { type: p.paymentType, number: p.apartmentNumber })
            : `${p.paymentType || 'Payment'} — ${p.tenantName || 'N/A'}`,
          amount: new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(p.amount || 0),
          status: 'completed',
          date: new Date(p.paymentDate).toLocaleDateString(),
          time: new Date(p.createdAt || p.paymentDate).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        })),
        alerts: []
      };

      // Populate Alerts based on data
      if (pendingCount > 0) {
        realData.alerts.push({
          id: 'alert-pending',
          type: 'warning',
          title: t('overdue_payments'),
          message: t('overdue_payments_msg', { count: pendingCount, amount: `$${pendingAmount}` }),
          action: t('view_details'),
          link: '/admin/payments?status=pending'
        });
      }

      if (statsRes.data?.upcomingLeases && statsRes.data.upcomingLeases.length > 0) {
        realData.alerts.push({
          id: 'alert-leases',
          type: 'info',
          title: t('lease_expiring_soon'),
          message: t('lease_expiring_msg', { count: statsRes.data.upcomingLeases.length }),
          action: t('review_leases'),
          link: '/admin/tenants'
        });
      }

      setDashboardData(realData);
      setError(null);
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(t('error_loading_dashboard'));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [dateRange]);

  const getStatCardColor = (color) => {
    const colors = {
      blue: isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400' : 'bg-zinc-50 text-red-600',
      green: isDarkMode ? 'bg-green-900 bg-opacity-20 text-green-400' : 'bg-green-50 text-green-600',
      purple: isDarkMode ? 'bg-purple-900 bg-opacity-20 text-purple-400' : 'bg-purple-50 text-purple-600',
      red: isDarkMode ? 'bg-red-900 bg-opacity-20 text-red-400' : 'bg-zinc-50 text-red-600'
    };
    return colors[color] || colors.blue;
  };

  const getChangeColor = (trend) => {
    return trend === 'up' ? 'text-green-500' : 'text-red-500';
  };

  const getTransactionTypeColor = (type) => {
    const colors = {
      payment: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-400',
      tenant: 'bg-zinc-100 text-red-800 dark:bg-red-900 dark:text-red-400',
      maintenance: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-400',
      owner: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-400'
    };
    return colors[type] || colors.payment;
  };

  const getAlertTypeColor = (type) => {
    const colors = {
      warning: 'bg-yellow-50 border-yellow-200 text-yellow-800 dark:bg-yellow-900 dark:border-yellow-800 dark:text-yellow-400',
      info: 'bg-zinc-50 border-red-200 text-red-800 dark:bg-red-900 dark:border-red-800 dark:text-red-400',
      success: 'bg-green-50 border-green-200 text-green-800 dark:bg-green-900 dark:border-green-800 dark:text-green-400',
      error: 'bg-zinc-50 border-red-200 text-red-800 dark:bg-red-900 dark:border-red-800 dark:text-red-400'
    };
    return colors[type] || colors.info;
  };

  const handleQuickAction = (action) => {
    const routes = {
      'add_property': '/admin/properties/add',
      'add_tenant': '/admin/tenants/add',
      'record_payment': '/admin/payments/add',
      'add_owner': '/admin/owners/add'
    };
    if (routes[action]) navigate(routes[action]);
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className={`${isDarkMode ? 'bg-gray-850' : 'bg-white'} rounded-xl p-6 animate-pulse`}>
          <div className="h-8 bg-gray-300 dark:bg-zinc-800 rounded w-1/3 mb-2"></div>
          <div className="h-4 bg-gray-300 dark:bg-zinc-800 rounded w-1/2"></div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className={`${isDarkMode ? 'bg-gray-850' : 'bg-white'} rounded-xl p-6 animate-pulse`}>
              <div className="h-4 bg-gray-300 dark:bg-zinc-800 rounded w-1/2 mb-4"></div>
              <div className="h-8 bg-gray-300 dark:bg-zinc-800 rounded w-1/3 mb-2"></div>
              <div className="h-3 bg-gray-300 dark:bg-zinc-800 rounded w-1/4"></div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`${isDarkMode ? 'bg-gray-850 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-md p-8 text-center`}>
        <RiAlertLine className="mx-auto mb-4 text-red-500" size={48} />
        <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
          {t('error_loading_dashboard')}
        </h3>
        <p className={`mb-4 ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>{error}</p>
        <button
          onClick={() => fetchDashboardData()}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
        >
          {t('retry')}
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <div className={`${isDarkMode ? 'bg-gray-850 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-md p-6`}>
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div className="flex items-center space-x-4">
            <div className={`p-3 rounded-full ${isDarkMode ? 'bg-red-900 bg-opacity-20' : 'bg-zinc-50'}`}>
              <RiDashboardLine className="text-red-500" size={32} />
            </div>
            <div>
              <h1 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('welcome_back')}, {user?.firstName}!
              </h1>
              <p className={`mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                {t('dashboard_subtitle')}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className={`px-3 py-2 text-sm rounded-lg border ${isDarkMode ? 'bg-zinc-900 border-gray-700 text-white' : 'bg-white border-gray-300 text-gray-900'}`}
            >
              <option value="7">{t('last_7_days')}</option>
              <option value="30">{t('last_30_days')}</option>
              <option value="90">{t('last_3_months')}</option>
              <option value="365">{t('last_year')}</option>
            </select>

            <button
              onClick={() => fetchDashboardData(true)}
              disabled={refreshing}
              className={`p-2 rounded-lg ${isDarkMode ? 'bg-zinc-900 hover:bg-zinc-800 text-gray-300' : 'bg-gray-100 hover:bg-gray-200 text-gray-600'} transition-colors disabled:opacity-50`}
            >
              <RiRefreshLine className={refreshing ? 'animate-spin' : ''} size={18} />
            </button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {dashboardData.stats.map((stat, index) => (
          <div
            key={index}
            className={`${isDarkMode ? 'bg-gray-850 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer`}
            onClick={() => {
              const isAdminOrManager = ['admin', 'manager'].includes(user?.role);
              if (stat.title.includes(t('total_properties'))) {
                navigate('/admin/buildings');
              } else if (stat.title.includes(t('active_tenants'))) {
                navigate('/admin/tenants');
              } else if (stat.title.includes(t('monthly_revenue'))) {
                if (isAdminOrManager) navigate('/admin/reports/revenue');
                else navigate('/admin/payments');
              } else if (stat.title.includes(t('pending_payments'))) {
                navigate('/admin/payments');
              }
            }}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <p className={`text-sm font-medium ${isDarkMode ? 'text-zinc-400' : 'text-gray-600'}`}>
                  {stat.title}
                </p>
                <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'} mt-1`}>
                  {stat.value}
                </p>
                <div className="flex items-center mt-2 space-x-2">
                  <div className="flex items-center">
                    {stat.trend === 'up' ? (
                      <RiArrowUpLine className={getChangeColor(stat.trend)} size={16} />
                    ) : (
                      <RiArrowDownLine className={getChangeColor(stat.trend)} size={16} />
                    )}
                    <span className={`text-sm font-medium ml-1 ${getChangeColor(stat.trend)}`}>
                      {stat.change}
                    </span>
                  </div>
                  <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-zinc-400'}`}>
                    {stat.description}
                  </span>
                </div>
              </div>
              <div className={`p-3 rounded-full ${getStatCardColor(stat.color)}`}>
                <stat.icon size={24} />
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Alerts Section */}
      {dashboardData.alerts.length > 0 && (
        <div className={`${isDarkMode ? 'bg-gray-850 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-md`}>
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'} flex items-center`}>
                <RiNotificationLine className="mr-2 text-orange-500" />
                {t('alerts_notifications')}
              </h3>
              <span className="bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-400 text-xs px-2 py-1 rounded-full">
                {dashboardData.alerts.length}
              </span>
            </div>
          </div>
          <div className="p-6 space-y-4">
            {dashboardData.alerts.map((alert) => (
              <div key={alert.id} className={`p-4 rounded-lg border ${getAlertTypeColor(alert.type)}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium">{alert.title}</h4>
                    <p className="text-sm mt-1">{alert.message}</p>
                  </div>
                  <button onClick={() => navigate(alert.link)} className="ml-4 text-sm font-medium hover:underline">
                    {alert.action}
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Transactions */}
        <div className={`lg:col-span-2 ${isDarkMode ? 'bg-gray-850 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-md`}>
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                {t('recent_transactions')}
              </h3>
              <button onClick={() => navigate('/admin/payments')} className={`text-sm ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-500'}`}>
                {t('view_all')}
              </button>
            </div>
          </div>
          <div className="p-6">
            <div className="space-y-4">
              {dashboardData.recentTransactions.length === 0 ? (
                <p className={`text-center py-8 text-sm ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>
                  {t('no_recent_transactions') || 'No recent transactions found.'}
                </p>
              ) : (
                dashboardData.recentTransactions.map((transaction) => (
                  <div key={transaction.id} className="flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-zinc-900 transition-colors">
                    <div className="flex items-center space-x-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${getTransactionTypeColor(transaction.type)}`}>
                        {transaction.type}
                      </span>
                      <div>
                        <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {transaction.description}
                        </p>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`text-xs ${isDarkMode ? 'text-zinc-400' : 'text-gray-500'}`}>{transaction.date}</span>
                          <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-zinc-400'}`}>{transaction.time}</span>
                        </div>
                      </div>
                    </div>
                    <div className="text-end">
                      <p className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{transaction.amount}</p>
                      <span className={`text-xs px-2 py-1 rounded-full ${transaction.status === 'completed'
                        ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-400'
                        : 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-400'}`}>
                        {transaction.status}
                      </span>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className={`${isDarkMode ? 'bg-gray-850 border-gray-700' : 'bg-white border-gray-200'} rounded-xl border shadow-md`}>
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
              {t('quick_actions')}
            </h3>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 gap-3">
              {[
                { key: 'add_property', icon: RiBuildingLine, color: 'blue' },
                { key: 'add_tenant', icon: RiTeamLine, color: 'green' },
                { key: 'record_payment', icon: RiMoneyDollarCircleLine, color: 'purple' },
                { key: 'add_owner', icon: RiUserLine, color: 'orange' }
              ].map((action) => (
                <button
                  key={action.key}
                  onClick={() => handleQuickAction(action.key)}
                  className={`p-4 rounded-lg border-2 border-dashed text-left transition-all ${isDarkMode
                    ? 'border-gray-700 hover:border-red-500 hover:bg-red-900 hover:bg-opacity-10'
                    : 'border-gray-300 hover:border-red-500 hover:bg-zinc-50'}`}
                >
                  <div className="flex items-center space-x-3">
                    <action.icon className="text-red-500" size={20} />
                    <span className={`text-sm font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      {t(action.key)}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
